import * as React from 'react';

const moreHorizontalSrc = 'https://assets.quill.org/images/icons/more-horizontal.svg'

interface ElementAction {
  element: JSX.Element;
  name?: never;
  action?: never;
}

interface NamedAction {
  name: string;
  action: Function;
  element?: never;
}

type Action = ElementAction | NamedAction;

interface ActionMenuProps {
  actions: Array<Action>;
  id?: number | string;
  isOpen: boolean;
  onToggle: () => void;
  buttonAriaLabel?: string;
  buttonClassName?: string;
  menuAriaLabel?: string;
  triggerElement?: JSX.Element;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({
  actions,
  id,
  isOpen,
  onToggle,
  buttonAriaLabel = "Actions",
  buttonClassName = "quill-button outlined extra-small grey actions-button focus-on-light",
  menuAriaLabel = "Actions menu container",
  triggerElement
}) => {
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  const handleClickOutside = React.useCallback((e: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      if (isOpen) {
        onToggle();
      }
    }
  }, [isOpen, onToggle]);

  const handleMenuBlur = React.useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (mousePressInsideRef.current) { return; }
    if (menuRef.current && !menuRef.current.contains(e.relatedTarget as Node)) {
      onToggle();
    }
  }, [onToggle]);

  const handleMenuKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onToggle();
    }
  }, [onToggle]);

  // Safari does not move focus to a button or link when it is clicked, so a mouse press on a
  // menu item blurs the auto-focused first item with relatedTarget null. handleMenuBlur would
  // read that as "focus left the menu" and close it on mousedown, before the click reaches the
  // item, so no menu action could ever fire in Safari. Remember that a mouse press started
  // inside the menu and ignore the blur that press causes; the flag clears once the press's
  // own focus changes have run, so a later Tab-away or click-outside still closes the menu.
  // Nothing about the press itself is cancelled, so text selection, link dragging and focusing
  // any custom element all keep their default behavior.
  const mousePressInsideRef = React.useRef(false);

  const handleMenuMouseDown = React.useCallback(() => {
    mousePressInsideRef.current = true;
    window.setTimeout(() => { mousePressInsideRef.current = false; }, 0);
  }, []);

  const clickAction = React.useCallback((action: Function) => {
    action(id);
    onToggle();
  }, [id, onToggle]);

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, false);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, [handleClickOutside]);

  const renderMenu = () => {
    if (!isOpen) return null;

    const actionElements = actions.map((act, index) => {
      if (act.element) {
        // a role="menu" may only contain menu items; consumers pass plain links here
        return React.cloneElement(act.element, { key: `element-${index}`, role: act.element.props.role ?? 'menuitem' });
      }

      // disabling autoFocus rule because this is a non-standard use case - when the user interacts with the action button, we aren't pulling them out of the dom order to focus the first element in the menu
      /* eslint-disable jsx-a11y/no-autofocus */
      return (
        <button
          aria-label={act.name}
          autoFocus={index === 0}
          className="action focus-on-light"
          key={`${act.name}-${index}`}
          onClick={() => clickAction(act.action)}
          role="menuitem"
          type="button"
        >
          {act.name}
        </button>
      );
      /* eslint-enable jsx-a11y/no-autofocus */
    });

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        className="actions-menu-wrapper"
        onBlur={handleMenuBlur}
        onKeyDown={handleMenuKeyDown}
        onMouseDown={handleMenuMouseDown}
        ref={menuRef}
      >
        <div aria-label={menuAriaLabel} className="actions-menu" role="menu">
          {actionElements}
        </div>
      </div>
    );
  };

  if (triggerElement) {
    const trigger = React.cloneElement(triggerElement, {
      'aria-label': buttonAriaLabel,
      'aria-expanded': isOpen,
      onClick: onToggle,
      role: 'button',
      tabIndex: 0
    });

    return (
      <React.Fragment>
        {trigger}
        {renderMenu()}
      </React.Fragment>
    );
  }

  // Default behavior for non-custom triggers: show menu OR button
  if (isOpen) {
    return renderMenu();
  }

  return (
    <button
      aria-label={buttonAriaLabel}
      className={buttonClassName}
      onClick={onToggle}
      type="button"
    >
      <img alt="More options" src={moreHorizontalSrc} />
    </button>
  );
};

export default ActionMenu;
